import type { ProfileView } from "../../../shared/types.ts";

/** What a resume's text says about the contact fields: only what it states plainly, nothing guessed. */
export interface Found {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  linkedin?: string;
  github?: string;
  website?: string;
  city?: string;
  state?: string;
}

const EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[a-z]{2,}/i;
const PHONE = /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/;
const LINKEDIN = /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/[\w-]+\/?/i;
const GITHUB = /(?:https?:\/\/)?(?:www\.)?github\.com\/[\w-]+\/?/i;
const SITE = /(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+\.(?:com|dev|io|me|co|net|org|app|xyz|ai|site|page)(?:\/[\w./-]*)?/gi;
const PLACE = /(?:^|[|•·]\s*)([A-Z][a-zA-Z.'-]+(?: [A-Z][a-zA-Z.'-]+){0,2}), ([A-Z]{2})\b/m;
const NAME_WORD = /^[A-Z][a-zA-Z'’-]+$|^[A-Z]{2,}$/;
const SKIP_SITE = /linkedin|github|gmail|outlook|yahoo|hotmail|icloud|proton/i;

const titleCase = (w: string) => (w === w.toUpperCase() ? w[0] + w.slice(1).toLowerCase() : w);
const clean = (u: string) => u.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "");

/** The name, email, phone and links on a resume's first lines, read from its text. */
export function findContact(text: string): Found {
  const out: Found = {};
  const email = text.match(EMAIL)?.[0];
  if (email) out.email = email;
  const phone = text.match(PHONE)?.[0];
  if (phone) out.phone = phone.trim();
  const linkedin = text.match(LINKEDIN)?.[0];
  if (linkedin) out.linkedin = clean(linkedin);
  const github = text.match(GITHUB)?.[0];
  if (github) out.github = clean(github);
  const site = [...text.replace(EMAIL, " ").matchAll(SITE)].map((m) => m[0]).find((u) => !SKIP_SITE.test(u));
  if (site) out.website = clean(site);

  const lines = text
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const place = lines.slice(0, 4).join("\n").match(PLACE);
  if (place) {
    out.city = place[1];
    out.state = place[2];
  }
  const first = lines[0];
  const words = first?.split(/\s+/).filter((w) => !/^[|•·,]$/.test(w)) ?? [];
  const stop = words.findIndex((w) => !NAME_WORD.test(w));
  const name = stop === -1 ? words : words.slice(0, stop);
  if (name.length >= 2 && name.length <= 4) {
    out.firstName = titleCase(name[0]!);
    out.lastName = name.slice(1).map(titleCase).join(" ");
  }
  return out;
}

/** Fills only the fields the user left blank, so a resume never overwrites what they typed. */
export function applyFound(p: ProfileView, f: Found): ProfileView {
  const keep = (a: string | undefined, b: string | undefined) => (a?.trim() ? a : (b ?? a ?? ""));
  return {
    ...p,
    firstName: keep(p.firstName, f.firstName),
    lastName: keep(p.lastName, f.lastName),
    email: keep(p.email, f.email),
    phone: keep(p.phone, f.phone),
    address: { ...p.address, city: keep(p.address?.city, f.city) || undefined, state: keep(p.address?.state, f.state) || undefined },
    links: {
      ...p.links,
      linkedin: keep(p.links.linkedin, f.linkedin) || undefined,
      github: keep(p.links.github, f.github) || undefined,
      website: keep(p.links.website, f.website) || undefined,
    },
  };
}

/** A resume's text in any format the app takes, with the readers loaded on demand so they stay out of the main bundle. A failed read is empty. */
export async function fileText(file: File): Promise<string> {
  try {
    const { resumeFileText } = await import("../../../shared/resume.ts");
    return (await resumeFileText(new Uint8Array(await file.arrayBuffer()), file.name)) ?? "";
  } catch {
    return "";
  }
}
